import type { Look } from '../types';
import { planLyrics, type LyricEnv, type LyricPlan } from './lyricLayout';

/**
 * 歌词层（DOM）。沿屏幕右上边缘竖排，按佛像剪影自动避让（见 lyricLayout）。
 * 每次换句都创建新元素并以「时间轴序号」为键，
 * 因此同一短句连续重复出现时，每一次都会重新触发入场动画。
 */
export class LyricsLayer {
  private current: HTMLElement | null = null;
  private currentKey = '';
  private currentText = '';
  private stage: HTMLElement;
  private anim: Look['text']['animation'] = 'bloom';
  private env: LyricEnv | null = null;
  private planSig = '';
  /** 最近一次排布结果（供调试面板与测试读取） */
  plan: LyricPlan | null = null;

  constructor(private readonly root: HTMLElement) {
    this.stage = document.createElement('div');
    this.stage.className = 'lyric-stage';
    root.append(this.stage);
  }

  applyLook(t: Look['text']): void {
    const s = this.root.style;
    s.setProperty('--ly-font', t.font);
    s.setProperty('--ly-weight', String(t.weight));
    s.setProperty('--ly-spacing', `${t.spacing}em`);
    s.setProperty('--ly-color', t.color);
    s.setProperty('--ly-glow-color', t.glowColor);
    s.setProperty('--ly-glow', String(t.glow));
    s.setProperty('--ly-dur', `${t.duration}s`);
    this.anim = t.animation;
  }

  /** 每帧调用：音频驱动的文字辉光 */
  setDrive(v: number): void {
    this.root.style.setProperty('--ly-drive', v.toFixed(3));
  }

  /** 布局环境（佛像位置、可用区域）变化时调用；当前句会立即重新排布 */
  setEnv(env: LyricEnv, sig: string): void {
    this.env = env;
    if (sig === this.planSig) return;
    this.planSig = sig;
    if (this.current) this.layout(this.current, this.currentText, false);
  }

  /** key 相同则不重复触发；key 不同（哪怕文字相同）则重新入场 */
  show(key: string, text: string): void {
    if (key === this.currentKey) return;
    this.currentKey = key;
    this.currentText = text;
    this.exitCurrent();
    const el = document.createElement('div');
    el.className = `lyric-line anim-${this.anim}`;
    el.dataset.key = key;
    el.setAttribute('aria-label', text);
    this.layout(el, text, true);
    this.stage.appendChild(el);
    // 强制回流后加 class，确保动画从头开始
    void el.offsetWidth;
    el.classList.add('in');
    this.current = el;
  }

  clear(): void {
    if (!this.currentKey) return;
    this.currentKey = '';
    this.currentText = '';
    this.exitCurrent();
  }

  /** 动画参数变化后重放当前句，便于在面板中预览 */
  replay(): void {
    if (!this.current) return;
    const key = this.currentKey;
    this.currentKey = '';
    this.show(key, this.currentText);
  }

  /** 当前显示的文字（测试 / 调试用） */
  get text(): string {
    return this.currentText;
  }

  private layout(el: HTMLElement, text: string, fresh: boolean): void {
    const env = this.env;
    if (!env) return;
    const plan = planLyrics(text, env);
    this.plan = plan;
    const sig = JSON.stringify([plan.right.cols, plan.left?.cols]);
    el.style.setProperty('--ly-size', `${plan.f}px`);
    el.style.setProperty('--ly-pitch', `${plan.pitch}px`);
    el.style.setProperty('--ly-gap', `${plan.gap}px`);
    el.classList.toggle('cramped', plan.cramped);
    if (fresh || el.dataset.sig !== sig) {
      el.dataset.sig = sig;
      el.textContent = '';
      let i = 0;
      const n = [...plan.right.cols, ...(plan.left?.cols ?? [])].join('').length;
      el.style.setProperty('--n', String(n));
      for (const [side, block] of [['right', plan.right], ['left', plan.left]] as const) {
        if (!block) continue;
        const b = document.createElement('div');
        b.className = `ly-block ${side}`;
        for (const col of block.cols) {
          const c = document.createElement('div');
          c.className = 'ly-col';
          for (const ch of col) {
            const span = document.createElement('span');
            span.className = 'ch';
            span.textContent = ch;
            span.style.setProperty('--i', String(i++));
            c.appendChild(span);
          }
          b.appendChild(c);
        }
        el.appendChild(b);
      }
    }
    const blocks = el.querySelectorAll<HTMLElement>('.ly-block');
    let k = 0;
    for (const block of [plan.right, plan.left]) {
      if (!block) continue;
      const b = blocks[k++];
      if (!b) continue;
      b.style.top = `${block.y}px`;
      b.style.height = `${block.h}px`;
      if (b.classList.contains('right')) {
        b.style.right = `${env.vw - block.x}px`;
        b.style.left = '';
      } else {
        b.style.left = `${block.x}px`;
        b.style.right = '';
      }
    }
  }

  private exitCurrent(): void {
    const old = this.current;
    this.current = null;
    if (!old) return;
    old.classList.remove('in');
    old.classList.add('out');
    const done = () => old.remove();
    old.addEventListener('animationend', (e) => e.target === old && done());
    setTimeout(done, 2500);
  }
}
